/**
 * Phase 2 · Album card status derivation helpers.
 *
 * Single source of truth for the three user-facing status labels
 * ("Album Only", "In Progress", "Completed") and the progress bar
 * visual state (0%, partial, 100%). Kept as a pure JS module so the
 * same logic can be unit-tested in Jest AND reused across every card
 * surface (MyAlbums, MyProfile / Your World Cup Collection,
 * CollectorProfilePreview, Museum).
 *
 * Contract:
 *   deriveAlbumStatus(album)  → { status, label, tone }
 *   deriveProgress(album)     → { percent, state, fillClass, trackClass }
 *
 * `album` shape (accepts both the dashboard-summary card and the
 * MyAlbums row):
 *   {
 *     status?: 'collecting' | 'completed' | 'archived' | 'sold',
 *     collection_method?: 'album_only' | 'sticker' | ...,
 *     completion?: { completion_percentage?: number, is_complete?: boolean,
 *                    total_stickers?: number },
 *     completion_percentage?: number  (MyAlbums flat form)
 *   }
 */

export const STATUS_ALBUM_ONLY = "album_only";
export const STATUS_IN_PROGRESS = "in_progress";
export const STATUS_COMPLETED = "completed";

const LABELS = {
  [STATUS_ALBUM_ONLY]: "Album Only",
  [STATUS_IN_PROGRESS]: "In Progress",
  [STATUS_COMPLETED]: "Completed",
};

const TONES = {
  [STATUS_ALBUM_ONLY]:  "album-only",
  [STATUS_IN_PROGRESS]: "in-progress",
  [STATUS_COMPLETED]:   "completed",
};

/**
 * Pick the percentage from either the dashboard card shape
 * (`completion.completion_percentage`) or the legacy MyAlbums row
 * shape (`completion_percentage` on the album root). Returns a number
 * between 0 and 100, or `null` when tracking is not applicable.
 */
export function readPercent(album) {
  if (!album || typeof album !== "object") return null;
  const c = album.completion;
  if (c && typeof c === "object") {
    const p = Number(c.completion_percentage);
    if (Number.isFinite(p)) return clamp(p);
  }
  const flat = Number(album.completion_percentage);
  if (Number.isFinite(flat)) return clamp(flat);
  return null;
}

function clamp(n) {
  if (n < 0) return 0;
  if (n > 100) return 100;
  return n;
}

/**
 * Derive the user-facing status pill for an album card.
 *
 * Precedence (spec):
 *   1. `collection_method === 'album_only'`   → "Album Only"
 *   2. Explicit `status === 'completed'`      → "Completed"
 *   3. `completion.is_complete === true`      → "Completed"
 *   4. Percentage === 100                      → "Completed"
 *   5. Anything else with a valid percent     → "In Progress"
 *   6. No completion data + not album-only    → "In Progress" (default)
 */
export function deriveAlbumStatus(album) {
  if (!album || typeof album !== "object") {
    return { status: STATUS_IN_PROGRESS, label: LABELS[STATUS_IN_PROGRESS], tone: TONES[STATUS_IN_PROGRESS] };
  }
  if (album.collection_method === "album_only") {
    return { status: STATUS_ALBUM_ONLY, label: LABELS[STATUS_ALBUM_ONLY], tone: TONES[STATUS_ALBUM_ONLY] };
  }
  if (album.status === "completed") {
    return { status: STATUS_COMPLETED, label: LABELS[STATUS_COMPLETED], tone: TONES[STATUS_COMPLETED] };
  }
  const c = album.completion;
  if (c && c.is_complete === true) {
    return { status: STATUS_COMPLETED, label: LABELS[STATUS_COMPLETED], tone: TONES[STATUS_COMPLETED] };
  }
  const pct = readPercent(album);
  if (pct !== null && pct >= 100) {
    return { status: STATUS_COMPLETED, label: LABELS[STATUS_COMPLETED], tone: TONES[STATUS_COMPLETED] };
  }
  return { status: STATUS_IN_PROGRESS, label: LABELS[STATUS_IN_PROGRESS], tone: TONES[STATUS_IN_PROGRESS] };
}

/**
 * Derive progress bar visual state.
 *
 * Returns three states:
 *   • empty    — pct === 0 or null and album is not "Album Only"
 *   • partial  — 0 < pct < 100 (red accent fill)
 *   • full     — pct === 100 (green fill)
 *   • hidden   — status is "Album Only" (no bar)
 */
export function deriveProgress(album) {
  const status = deriveAlbumStatus(album).status;
  if (status === STATUS_ALBUM_ONLY) {
    return { percent: null, state: "hidden", fillClass: "", trackClass: "" };
  }
  const raw = readPercent(album);
  const percent = raw === null ? 0 : Math.round(raw);
  const trackClass = "bg-[var(--stone)]";
  if (percent >= 100) {
    return {
      percent: 100,
      state: "full",
      fillClass: "bg-emerald-500",
      trackClass,
    };
  }
  if (percent > 0) {
    return {
      percent,
      state: "partial",
      fillClass: "bg-[var(--panini-red)]",
      trackClass,
    };
  }
  return {
    percent: 0,
    state: "empty",
    fillClass: "bg-[var(--panini-red)]",
    trackClass,
  };
}

/**
 * Combined convenience helper used by tests.
 * Returns `{ status, label, tone, progress }`.
 */
export function albumCardModel(album) {
  const s = deriveAlbumStatus(album);
  const p = deriveProgress(album);
  return { ...s, progress: p };
}
